import React, { useMemo, Dispatch, SetStateAction } from 'react'
import { makeStyles } from 'tss-react/mui'
import { Formik } from 'formik'
import { object, lazy, string } from 'yup'
import { Box, Button } from '@mui/material'
import { isString, isNumber } from 'lodash'

import { AvailableTest, ParamType } from 'src/Libs/installation-details/dto/hardware-test-result.dto'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { numberSchema } from 'src/Configuration/validation/rules'
import { TriggerHardwareTestParamsData } from '../../../../models'
import ParamInput from './ParamInput'
import { hardwareTestSubmitDateFormat } from '../../../../const'
import { getTranslatedDefaultValue } from '../../../../utils'
import { format, formatISO } from '@e3dc-react/shell/Libs/date'

const useStyles = makeStyles()(theme => ({
  contentContainer: {
    width: '100%',
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    alignItems: 'center',
    minHeight: '260px',
    marginTop: theme.spacing(2),
    '@media (max-width: 650px)': {
      paddingInline: theme.spacing(2),
    },
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    backgroundColor: theme.palette.background.paper2,
    border: `1px solid ${theme.palette.background.paperBorder}`,
    borderRadius: theme.shape.borderRadius,
    paddingBlock: theme.spacing(2),
    paddingInline: theme.spacing(4),
    marginTop: theme.spacing(2),
    width: '100%',
    maxWidth: '600px',
    '@media (max-width: 650px)': {
      textAlign: 'center',
    },
  },
  button: {
    marginBlock: theme.spacing(4),
  },
  constrols: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
}))

interface Props {
  selectedTest: AvailableTest
  handleSubmit: (data: TriggerHardwareTestParamsData) => void
  setShouldProvideParameters: Dispatch<SetStateAction<boolean>>
}

/**
 * Returns formik form and gives possibility to set parameter values for current selected test
 */
const TestParametersForm: React.FC<Props> = ({ selectedTest, handleSubmit, setShouldProvideParameters }) => {
  const { classes } = useStyles()
  const { t, selectedAppLanguage } = useCustomTranslation()

  /**
   * Defines initial values for the params
   */
  const initialValues = useMemo(() => {
    if (!selectedTest.params.length) {
      return {}
    }

    return selectedTest.params.reduce((total, current) => {
      const { key, defaultValue, possibleValues, paramType } = current
      const isValidDefaultValue = isString(defaultValue) || isNumber(defaultValue)

      // Parameter fields should be populated by default values if it's available
      if (!defaultValue) {
        return { ...total, [key]: isValidDefaultValue ? defaultValue : '' }
      }

      const translatedDefaultValue = getTranslatedDefaultValue(defaultValue, t)

      // Param with enum type and only two possible values is boolean type
      const isBooleanType = paramType === ParamType.ENUM && possibleValues?.length === 2
      if (isBooleanType) {
        // first possible value counts as a positive value -> true
        const positiveValue = possibleValues?.[0]
        const translatedPositiveValue = getTranslatedDefaultValue(positiveValue, t)

        const booleanDefaultValue = translatedPositiveValue === translatedDefaultValue

        return { ...total, [key]: booleanDefaultValue }
      }

      return { ...total, [key]: translatedDefaultValue }
    }, {})

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTest.params, selectedAppLanguage])

  /**
   * Defines validation schema based on param type
   */
  const validationSchema = useMemo(() => {
    if (selectedTest.params.length) {
      const numberSchemaWithTranslation = numberSchema(t)

      const validators = selectedTest.params.reduce((total, current) => {
        const { key, paramType, min, max, required = true } = current

        switch (paramType) {
          case ParamType.ENUM:
          case ParamType.TEXT:
          case ParamType.DATETIME:
          case ParamType.DATE: {
            const value = lazy(data => {
              if (!!data && typeof data === 'object') {
                return object()
              }

              return required ? string().required() : string()
            })

            return { ...total, [key]: value }
          }
          case ParamType.NUMBER:
          case ParamType.NUMBERRANGE: {
            const numberSchema = required ? numberSchemaWithTranslation({ min, max }).required() : numberSchemaWithTranslation({ min, max })
            return { ...total, [key]: numberSchema.nullable() }
          }
          default:
            return total
        }
      }, {})

      return object().shape(validators)
    }

    return {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTest.params, selectedAppLanguage])

  const onSubmit = (values: TriggerHardwareTestParamsData): void => {
    const typeByKey = Object.fromEntries(selectedTest.params.map(elem => [elem.key, elem.paramType]))
    // Param with enum type and only two possible values is boolean type
    const booleanFieldsKeys = selectedTest.params
      .filter(({ paramType, possibleValues }) => paramType === ParamType.ENUM && possibleValues?.length === 2)
      .map(elem => elem.key)

    const formDataEntries = Object.entries(values || {}).map(elem => {
      const [key, value] = elem

      switch (typeByKey[key]) {
        case ParamType.NUMBERRANGE:
        case ParamType.NUMBER: {
          return [key, +value]
        }
        case ParamType.DATE: {
          return [key, format(new Date(value as string), hardwareTestSubmitDateFormat)]
        }
        case ParamType.DATETIME: {
          return [key, formatISO(new Date(value as string))]
        }
        case ParamType.ENUM: {
          if (booleanFieldsKeys.includes(key)) {
            const possibleValues =
              selectedTest?.params.find(elem => elem.key === key)?.possibleValues?.map(elem => getTranslatedDefaultValue(elem, t)) ?? []

            return [key, possibleValues?.[+!value]]
          }
          return [key, value]
        }
        default:
          return [key, value]
      }
    })

    handleSubmit(Object.fromEntries(formDataEntries))
  }

  const goBack = (): void => {
    setShouldProvideParameters(false)
  }

  return (
    <Formik initialValues={initialValues} onSubmit={onSubmit} validationSchema={validationSchema}>
      {({ handleSubmit }) => {
        return (
          <form className={classes.contentContainer} onSubmit={handleSubmit}>
            <Box className={classes.card}>
              {selectedTest.params.map(paramData => (
                <ParamInput paramData={paramData} key={paramData.key} />
              ))}
            </Box>
            <Box className={classes.constrols}>
              <Button className={classes.button} variant="text" onClick={goBack}>
                {t('system-details.hardware-test.back-button')}
              </Button>
              <Button className={classes.button} type="submit" variant="contained">
                {t('system-details.hardware-test.run-test')}
              </Button>
            </Box>
          </form>
        )
      }}
    </Formik>
  )
}

export default TestParametersForm
